import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Checkbox,
  FormControlLabel,
} from "@mui/material";
import CommonLoader from "../../common/CommonLoader";
import { PrintUserRouteCoC } from "../../../services/UserRoutesService";
import { NotificationManager } from "react-notifications";
import { GetMultiSelectClients } from "../../../services/ClientsMaintenanceService";

const ClientCoCPrintPopup = ({ open, routeClients, onClose }) => {
  const [selectedClients, setSelectedClients] = useState([]);
  const [loading, setLoading] = useState(false);
  const [clients, setClients] = useState([]);

  // Fetch pool clients on open
  useEffect(() => {
    if (open && routeClients) {
      fetchData();
    }
  }, [open, routeClients]);

  const fetchData = async () => {
    try {
      setLoading(true);

      const response = await GetMultiSelectClients("pool");

      const allPoolClients = response.results.map((client) => ({
        accountNumber: client.accountNumber.trim(),
        clientName: client.clientName.trim(),
        routeCollection: (client.routeCollection || "").trim(),
      }));

      const routeClientIds = routeClients
        .split(",")
        .map((c) => c.trim());

      const routePoolClients = allPoolClients.filter(
        (client) =>
          routeClientIds.includes(client.accountNumber) &&
          client.routeCollection === "Collect"
      );

      setClients(routePoolClients);

      // Preselect filtered clients
      setSelectedClients(routePoolClients.map(c => c.accountNumber));

    } catch (error) {
      NotificationManager.error(
        "Error fetching clients:",
        error.message || error
      );
    } finally {
      setLoading(false);
    }
  };

  // Reset state when dialog closes
  useEffect(() => {
    if (!open) {
      setSelectedClients([]);
      setClients([]);
    }
  }, [open]);

  const handleClientToggle = (accountNumber) => {
    setSelectedClients((prevSelected) =>
      prevSelected.includes(accountNumber)
        ? prevSelected.filter((c) => c !== accountNumber)
        : [...prevSelected, accountNumber]
    );
  };

  const handleConfirm = async () => {
    setLoading(true);
    try {
      if (selectedClients.length === 0) {
        NotificationManager.warning("Please select at least one pool client");
        return;
      }
      const clientIds = selectedClients.join(",");
      await PrintUserRouteCoC(clientIds);

      onClose();
      NotificationManager.success("CoCs downloaded successfully");
    } catch (error) {
      NotificationManager.error("Error generating CoC:", error.message || error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <Dialog
        open={open}
        onClose={() => onClose(null)}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            maxHeight: "80vh", // limits dialog height
          },
        }}
      >
        <DialogTitle>Select Clients</DialogTitle>

        <DialogContent
          dividers
          sx={{
            maxHeight: "60vh",
            overflowY: "auto",
          }}
        >
          {clients.map((client) => (
            <FormControlLabel
              key={client.accountNumber}
              sx={{ mt: 1 }}
              control={
                <Checkbox
                  checked={selectedClients.includes(client.accountNumber)}
                  onChange={() => handleClientToggle(client.accountNumber)}
                />
              }
              label={`${client.accountNumber} - ${client.clientName}`}
            />
          ))}
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => onClose(null)}
            color="secondary"
            variant="outlined"
          >
            Cancel
          </Button>
          <Button
            onClick={handleConfirm}
            color="primary"
            variant="contained"
          >
            Print
          </Button>
        </DialogActions>
      </Dialog>
      <CommonLoader loading={loading} />
    </>
  );
};

export default ClientCoCPrintPopup;